import React, { useState, useEffect, useRef, useMemo } from "react";
import { serverFetch } from "../utils/serverApi";
import "./Mail.css";

const POLL_MS = 3000;
const MAX_POLL_ERRORS = 5;

const MailBroadcastPage = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState("all"); // all | within | before
  const [unit, setUnit] = useState("days");
  const [value, setValue] = useState("7");
  const [beforeDate, setBeforeDate] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [counting, setCounting] = useState(false);
  const [countResult, setCountResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcast, setBroadcast] = useState(null); // {id, status, counts}
  const [error, setError] = useState("");
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, forceTick] = useState(0);

  const pollTimer = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(pollTimer.current);
    };
  }, []);

  // Tick once a second during a rate-limit cooldown so the buttons re-enable.
  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const t = setInterval(() => {
      forceTick((n) => n + 1);
      if (Date.now() >= cooldownUntil) setCooldownUntil(0);
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  const audienceError = useMemo(() => {
    if (mode === "within") {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 1) return "Enter a whole number of 1 or more.";
    }
    if (mode === "before" && !beforeDate) return "Pick a date.";
    return "";
  }, [mode, value, beforeDate]);

  const buildAudience = () => {
    const audience = { platform: "app" };
    if (mode === "within") audience.signedUpWithin = { unit, value: Number(value) };
    if (mode === "before") audience.signedUpBefore = new Date(`${beforeDate}T00:00:00`).toISOString();
    if (verifiedOnly) audience.emailVerifiedOnly = true;
    return audience;
  };

  // Any audience change invalidates the previous count.
  useEffect(() => {
    setCountResult(null);
  }, [mode, unit, value, beforeDate, verifiedOnly]);

  const handleError = (e) => {
    setError(e.message || "Something went wrong.");
    if (e.status === 429) setCooldownUntil(Date.now() + (e.retryAfter || 30) * 1000);
  };

  const countRecipients = async () => {
    setError("");
    setCountResult(null);
    setCounting(true);
    try {
      const data = await serverFetch("/api/admin/mail/broadcast", {
        method: "POST",
        body: {
          subject: subject.trim() || "(preview)",
          message: message.trim() || "(preview)",
          audience: buildAudience(),
          dryRun: true,
        },
      });
      if (mounted.current) setCountResult(data);
    } catch (e) {
      if (mounted.current) handleError(e);
    } finally {
      if (mounted.current) setCounting(false);
    }
  };

  const poll = (id, errorsInARow = 0) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const data = await serverFetch(`/api/admin/mail/broadcast/${encodeURIComponent(id)}`);
        if (!mounted.current) return;
        setBroadcast({ id, status: data.status, counts: data.counts });
        if (data.status === "processing") poll(id);
      } catch (e) {
        if (!mounted.current) return;
        if (errorsInARow + 1 >= MAX_POLL_ERRORS) {
          setError(
            `Lost contact with the server while checking progress (${e.message}) The send may still be running. Broadcast ID: ${id}`
          );
          setBroadcast((b) => (b ? { ...b, status: "unknown" } : b));
        } else {
          poll(id, errorsInARow + 1);
        }
      }
    }, POLL_MS * (errorsInARow + 1));
  };

  const send = async () => {
    if (sending) return;
    setConfirmOpen(false);
    setSending(true);
    setError("");
    try {
      const data = await serverFetch("/api/admin/mail/broadcast", {
        method: "POST",
        body: { subject: subject.trim(), message: message.trim(), audience: buildAudience() },
      });
      if (!mounted.current) return;
      const status = data.status || "processing";
      setBroadcast({ id: data.broadcastId, status, counts: null });
      if (status === "processing") poll(data.broadcastId);
    } catch (e) {
      if (mounted.current) handleError(e);
    } finally {
      if (mounted.current) setSending(false);
    }
  };

  const running = !!broadcast && broadcast.status === "processing";
  const canCount = !counting && !running && !audienceError && cooldownLeft === 0;
  const canSend =
    !sending && !running && cooldownLeft === 0 && !!countResult && countResult.valid_email > 0 &&
    !!subject.trim() && !!message.trim() && !audienceError;

  const reset = () => {
    clearTimeout(pollTimer.current);
    setBroadcast(null);
    setCountResult(null);
  };

  const counts = broadcast ? broadcast.counts : null;

  return (
    <div className="mail-page">
      <h1>Send Mail to App Users</h1>

      <label htmlFor="mb-subject">Subject</label>
      <input id="mb-subject" type="text" maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={running} />

      <label htmlFor="mb-message">Message</label>
      <textarea id="mb-message" value={message} onChange={(e) => setMessage(e.target.value)} disabled={running} />

      <label htmlFor="mb-mode">Audience (app users)</label>
      <select id="mb-mode" value={mode} onChange={(e) => setMode(e.target.value)} disabled={running}>
        <option value="all">All app users</option>
        <option value="within">Signed up within the last...</option>
        <option value="before">Signed up before a date</option>
      </select>
      {mode === "within" && (
        <div className="mail-row" style={{ marginTop: ".5rem" }}>
          <input type="number" min="1" step="1" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Amount" disabled={running} />
          <select value={unit} onChange={(e) => setUnit(e.target.value)} aria-label="Unit" disabled={running}>
            <option value="days">days</option>
            <option value="weeks">weeks</option>
            <option value="months">months</option>
            <option value="years">years</option>
          </select>
        </div>
      )}
      {mode === "before" && (
        <input type="date" style={{ marginTop: ".5rem" }} value={beforeDate} onChange={(e) => setBeforeDate(e.target.value)} disabled={running} />
      )}
      {audienceError && <p className="mail-hint" style={{ color: "#dc2626" }}>{audienceError}</p>}

      <label style={{ display: "flex", gap: ".5rem", alignItems: "center", fontWeight: 400 }}>
        <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} disabled={running} />
        Verified email addresses only
      </label>

      {error && <div className="mail-banner err" role="alert">{error}</div>}
      {cooldownLeft > 0 && <div className="mail-hint">Rate limited. You can retry in {cooldownLeft}s.</div>}

      <div className="mail-actions">
        <button type="button" className="mail-btn" onClick={countRecipients} disabled={!canCount}>
          {counting ? "Counting..." : "Count recipients"}
        </button>
        <button type="button" className="mail-btn primary" onClick={() => setConfirmOpen(true)} disabled={!canSend}>
          {sending ? "Sending..." : "Send"}
        </button>
      </div>
      {!countResult && !broadcast && (
        <p className="mail-hint">Run Count recipients first. Send stays disabled until the current audience has been counted.</p>
      )}

      {countResult && (
        <div className="mail-box">
          <strong>{countResult.valid_email}</strong> valid recipient(s) out of {countResult.total_email} matched
          {countResult.invalid_email > 0 && <> ({countResult.invalid_email} invalid, will be skipped)</>}.
          {Array.isArray(countResult.sample) && countResult.sample.length > 0 && (
            <div className="mail-hint">
              Sample: {countResult.sample.map((s) => (typeof s === "string" ? s : s.email || s.name || JSON.stringify(s))).join(", ")}
            </div>
          )}
          {countResult.valid_email === 0 && <div className="mail-hint">No valid recipients, so nothing can be sent.</div>}
        </div>
      )}

      {broadcast && (
        <div className="mail-box" role="status">
          <div>
            <strong>{running ? "Broadcast in progress" : `Broadcast finished: ${broadcast.status}`}</strong>
          </div>
          <div className="mail-hint">ID: {broadcast.id}</div>
          {running && <div className="mail-progress"><div style={{ width: "100%", opacity: 0.4 }} /></div>}
          {counts ? (
            <ul style={{ marginTop: ".5rem" }}>
              <li>Total matched: {counts.total_email}</li>
              <li>Valid: {counts.valid_email}</li>
              <li>Invalid: {counts.invalid_email}</li>
              <li>Failed: {counts.failed}</li>
            </ul>
          ) : (
            <div className="mail-hint">Waiting for the first progress update...</div>
          )}
          {!running && (
            <div className="mail-actions">
              <button type="button" className="mail-btn" onClick={reset}>Compose another</button>
            </div>
          )}
        </div>
      )}

      {confirmOpen && (
        <div className="mail-modal-back" role="dialog" aria-modal="true" aria-labelledby="mb-confirm-title">
          <div className="mail-modal">
            <h2 id="mb-confirm-title" style={{ fontWeight: 700, fontSize: "1.1rem" }}>Send this email?</h2>
            <p style={{ margin: ".75rem 0" }}>
              This will email <strong>{countResult ? countResult.valid_email : 0}</strong> user(s) with the subject "{subject.trim()}". This cannot be undone.
            </p>
            <div className="mail-actions" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="mail-btn" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button type="button" className="mail-btn primary" onClick={send}>
                Send to {countResult ? countResult.valid_email : 0}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailBroadcastPage;
